import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Flex, Typography } from 'antd';
import Markdown from 'react-markdown';
import { setHeroTitle, setHeroContent, setHeroImage } from '../redux/hero.slice';
import Conatiner from '../shared/Conatiner/Container';
import { SUPPORT_EMAIL, SUPPORT_PORTAL_URL } from '../shared/supportDetails';
import useMobileCheck from '../mobile';
import './Policy.css';

const { Title, Paragraph } = Typography;

const lastUpdate = '2 September 2026';

const terms = `
#### 1. About these terms

These terms apply when you use the ShuttleOps website, web app, mobile apps, and related services. By using ShuttleOps, you agree to follow these terms. If you use ShuttleOps for a club, you confirm that you have permission to act for that club.

These terms do not remove any rights that applicable law gives you.

#### 2. Accounts and club access

You must give accurate account information and keep your login details secure. Tell us promptly if you think somebody has accessed your account without permission.

Club owners and administrators control access to their clubs. They can add players, assign roles, manage sessions, and maintain club records. Club representatives must have permission to add and use another person's information.

#### 3. Using ShuttleOps fairly

You must use ShuttleOps only for lawful club and playing activities. Do not:

- Try to access another person's account or a club without permission.
- Interfere with the service, its security, or other users.
- Upload harmful code or use the service to harm another person.
- Copy or misuse ShuttleOps software, branding, or content without permission.

We may restrict access when needed to protect users, clubs, or the service.

#### 4. Club information and decisions

Clubs are responsible for the information they enter and the decisions their administrators make. This includes membership, session, attendance, payment, and communication records.

ShuttleOps provides tools for club administration and player activity. Each club remains responsible for its own rules, events, safeguarding, and financial decisions.

#### 5. Plans and payments

ShuttleOps offers free and paid plans. Current plan details appear on the pricing page and in the app. Paid plans are billed monthly unless the relevant offer says otherwise.

Stripe processes payments and Direct Debit details. ShuttleOps does not store full bank account numbers or sort codes. Clubs can also record offline payments in the service.

If a price or charge is unclear, contact us before making the payment. Your club may also set its own session or membership charges.

#### 6. Service availability and changes

We work to keep ShuttleOps available and secure. Maintenance, faults, provider outages, or necessary updates can interrupt access.

We may improve, replace, or stop features. We will give reasonable notice when a material change affects normal use, where practical.

#### 7. Privacy and support

Our [Privacy Policy](/privacy) explains how ShuttleOps uses personal information and service providers.

For help, account closure, or data requests, email **${SUPPORT_EMAIL}** or use the [ShuttleOps Support Portal](${SUPPORT_PORTAL_URL}). Some records may need to remain where law or an active payment dispute requires this.

#### 8. Ending use

You can stop using ShuttleOps at any time. Club access can also end when a club administrator removes you or closes the club.

We may suspend or close access after serious or repeated misuse. We will act reasonably and give notice where practical.

#### 9. Changes to these terms

We may update these terms as ShuttleOps changes. We will update the date on this page and notify users of material changes where appropriate.

#### 10. Contact

Questions about these terms can go to **${SUPPORT_EMAIL}** or the [ShuttleOps Support Portal](${SUPPORT_PORTAL_URL}).
`;

const TermsPage = () => {
  const dispatch = useDispatch();
  const { isMobile } = useMobileCheck();

  useEffect(() => {
    dispatch(setHeroImage(false));
    dispatch(setHeroTitle('Clear terms for clubs and players'));
    dispatch(setHeroContent('The practical rules for accounts, club data, payments, and fair use of ShuttleOps.'));
  }, [dispatch]);

  return (
    <Flex className="policy-page" vertical gap={20}>
      <Conatiner colour="white">
        <Flex className="policy-summary" vertical gap={10}>
          <Title level={3}>Terms of Service</Title>
          <Paragraph>Last updated: {lastUpdate}</Paragraph>
          <Paragraph>
            Use a secure account, respect club members, and use ShuttleOps only for lawful club activities.
          </Paragraph>
        </Flex>
      </Conatiner>

      <Conatiner colour="white">
        <Flex
          className="policy-copy"
          style={{ padding: isMobile ? '20px' : '40px' }}
          vertical
          gap={12}
        >
          <Markdown
            components={{
              h4: (props) => <Title level={3}>{props.children}</Title>,
              ul: (props) => <ul>{props.children}</ul>,
              p: (props) => <Paragraph>{props.children}</Paragraph>,
            }}
          >
            {terms}
          </Markdown>
        </Flex>
      </Conatiner>
    </Flex>
  );
};

export default TermsPage;
